import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  FiZap, FiClock, FiAlertCircle, FiRefreshCw, FiChevronDown, FiChevronUp,
  FiShield, FiInfo,
} from 'react-icons/fi';

/**
 * Oeffentliche Versorger-Vergleichsliste (/v/:token) — ohne Login.
 *
 * Drei bewusste Entscheidungen gegenueber vergleichbaren Ansichten im Markt:
 *
 * 1. SORTIERSCHLUESSEL = ANGEZEIGTE KENNZAHL. Wonach sortiert wird, steht auch
 *    gross an der Zeile. Wird nach Energiekosten/Jahr sortiert, ist die grosse
 *    Zahl genau diese. Sonst fuehrt die Rangfolge in die Irre.
 * 2. ANONYMISIERUNG. Vor der Beauftragung liefert der Server die Versorgernamen
 *    gar nicht erst aus — hier steht dann "Anbieter A", "Anbieter B".
 * 3. VERGLICHEN WIRD DER ENERGIEPREIS, nicht der Gesamtpreis. Netzentgelte,
 *    Abgaben und Umlagen sind bei jedem Versorger identisch und deshalb nicht
 *    Teil des Vergleichs. Damit daraus kein falscher Eindruck entsteht, steht
 *    an JEDER Preisangabe "zzgl. Netzentgelte, Abgaben und Umlagen" — nicht nur
 *    im Kleingedruckten.
 */

/** Steht an jeder Preisangabe. Eine niedrige Zahl ohne diesen Zusatz waere irrefuehrend. */
const ZZGL = 'zzgl. Netzentgelte, Abgaben und Umlagen';

const fmtEUR = (n) =>
  n == null ? '–' : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtCt = (n, nk = 3) =>
  n == null ? '–' : n.toLocaleString('de-DE', { minimumFractionDigits: nk, maximumFractionDigits: nk }) + ' ct/kWh';
const fmtNum = (n) => (n == null ? '–' : Number(n).toLocaleString('de-DE'));
const fmtDate = (d) => {
  if (!d) return '–';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(d);
};

/** "20.08.2026 um 16:00 Uhr" — ohne Uhrzeit nur das Datum. */
const fmtFrist = (d, uhr) => (d ? (uhr ? `${fmtDate(d)} um ${uhr} Uhr` : fmtDate(d)) : '–');

/**
 * Bindefristen sind stundengenau. Ohne Uhrzeit gilt der Preis bis Tagesende —
 * sonst waere ein Angebot mit Frist "heute" schon um 00:01 abgelaufen.
 */
const fristAbgelaufen = (d, uhr) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d || ''));
  if (!m) return false;
  const [y, mo, t] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const zeit = /^(\d{1,2}):(\d{2})$/.exec(String(uhr || ''));
  const ende = zeit
    ? new Date(y, mo - 1, t, Number(zeit[1]), Number(zeit[2]), 0, 0)
    : new Date(y, mo - 1, t, 23, 59, 59, 999);
  return ende.getTime() < Date.now();
};

// Sortierschluessel und grosse Kennzahl sind DIESELBE Groesse — 'feld' wird fuer
// beides benutzt. Die Einheit haengt am Schluessel, damit die Anzeige nicht
// auseinanderlaufen kann.
const SORTIERUNGEN = [
  {
    key: 'energiekosten', label: 'Energiekosten/Jahr', badge: 'Guenstigste Energiekosten',
    feld: 'energiekosten_jahr_eur', format: fmtEUR,
  },
  {
    key: 'energiepreis', label: 'Energiepreis', badge: 'Guenstigster Energiepreis',
    feld: 'angebotspreis_ct_kwh', format: (v) => fmtCt(v),
  },
];

const TARIF_LABELS = {
  preistyp: 'Preismodell',
  mindestlaufzeit_monate: 'Mindestlaufzeit',
  lieferende_fest: 'Lieferende',
  mengenflexibilitaet: 'Mengenflexibilitaet',
  abrechnung: 'Abrechnung',
  bonitaet: 'Bonitaetsanforderung',
  buendelung_max_abnahmestellen: 'Buendelung',
  sonstiges: 'Sonstiges',
};

const tarifWert = (key, wert) => {
  if (wert === null || wert === undefined || wert === '') return null;
  if (key === 'preistyp') {
    if (wert === 'mischpreis') return 'Mischpreis — ein Energiepreis fuer die gesamte Laufzeit';
    if (wert === 'kalenderjahrespreis') return 'Kalenderjahrespreis — jedes Lieferjahr hat einen eigenen Preis';
    return String(wert);
  }
  if (key === 'lieferende_fest') return wert ? 'Festes Lieferende zum 31.12.' : 'Kein festes Lieferende';
  if (key === 'mindestlaufzeit_monate') return `${wert} Monate`;
  if (key === 'buendelung_max_abnahmestellen') return `bis zu ${wert} Abnahmestellen buendelbar`;
  return String(wert);
};

const Eckdatum = ({ label, wert }) => (
  <div>
    <p className="text-xs text-gray-400">{label}</p>
    <p className="text-sm font-medium text-gray-900 mt-0.5">{wert}</p>
  </div>
);

const VersorgerVergleich = () => {
  const { token } = useParams();
  const [daten, setDaten] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [nichtGefunden, setNichtGefunden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortierung, setSortierung] = useState('energiekosten');
  const [offen, setOffen] = useState({});

  const laden = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setNichtGefunden(false);
    try {
      const resp = await fetch(`/api/vergleich/${encodeURIComponent(token)}`);
      if (resp.status === 404) {
        setNichtGefunden(true);
        setDaten(null);
        return;
      }
      const json = await resp.json().catch(() => null);
      if (!resp.ok || !json) throw new Error('Server antwortet nicht wie erwartet');
      setDaten(json);
    } catch (err) {
      setDaten(null);
      setLoadError(err.message || 'Unbekannter Fehler');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { laden(); }, [laden]);

  const aktiveSortierung = SORTIERUNGEN.find(s => s.key === sortierung) || SORTIERUNGEN[0];

  // Sortiert wird nach der Groesse, die auch gross angezeigt wird.
  // Angebote ohne Wert stehen am Ende — sie sind nicht "guenstig", sondern unvollstaendig.
  const sortiert = useMemo(() => {
    const liste = [...(daten?.angebote || [])];
    const feld = aktiveSortierung.feld;
    liste.sort((a, b) => {
      const av = a[feld], bv = b[feld];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av - bv;
    });
    return liste;
  }, [daten, aktiveSortierung]);

  // Unterschiedliche Laufzeiten sind nicht unmittelbar vergleichbar.
  const laufzeiten = useMemo(
    () => [...new Set((daten?.angebote || []).map(a => a.laufzeit_monate).filter(Boolean))],
    [daten]
  );
  const gemischteLaufzeiten = laufzeiten.length > 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (nichtGefunden) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <FiAlertCircle className="mx-auto text-gray-400 mb-4" size={40} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Diese Uebersicht ist nicht verfuegbar</h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Moeglicherweise wurde der Link zurueckgezogen oder ist nicht mehr aktuell.
            Ihr Ansprechpartner bei AIQCON sendet ihn Ihnen gerne erneut zu.
          </p>
          <a href="mailto:energie@aiqcon.com" className="inline-block mt-5 px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium">
            energie@aiqcon.com
          </a>
        </div>
      </div>
    );
  }

  if (loadError || !daten) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <FiAlertCircle className="mx-auto text-gray-400 mb-4" size={40} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Die Uebersicht ist gerade nicht abrufbar</h1>
          <p className="text-sm text-gray-600 mb-5">Bitte versuchen Sie es in Kuerze noch einmal.</p>
          <button onClick={laden} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium">
            <FiRefreshCw size={15} /> Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  const anonym = !daten.versorger_sichtbar;
  // Der Server sagt, WORAUF sich die Preise beziehen. Nur wenn er ausdruecklich
  // den Energiepreis liefert, darf diese Seite behaupten, dass Netzentgelte,
  // Abgaben und Umlagen nicht enthalten sind — eine falsche Zusage waere hier
  // teurer als eine fehlende.
  const energiebasis = daten.preisbasis === 'energie';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Kopf — AIQCON-gebrandet */}
      <header className="bg-gradient-to-br from-teal-700 to-teal-500 text-white">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
            <FiZap size={16} />
            <span>AIQCON Energy · Ihr unabhaengiger Energiemakler</span>
          </div>
          <h1 className="text-3xl font-bold">Versorger-Vergleich</h1>
          <p className="text-white/90 mt-1">
            {daten.company_name}
            {daten.nummer ? <span className="text-white/60"> · {daten.nummer}</span> : null}
          </p>
          <p className="text-white/80 text-sm mt-4 max-w-2xl leading-relaxed">
            Wir haben fuer Sie mehrere Versorger angefragt. Hier sehen Sie die eingegangenen
            Rueckmeldungen im direkten Vergleich — mit allen Konditionen, die ueber den Preis hinaus
            zaehlen. Verglichen wird der Energiepreis, {ZZGL}.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Eckdaten */}
        <div className="bg-white rounded-xl border p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <Eckdatum label="Verbrauch" wert={`${fmtNum(daten.verbrauch_kwh)} kWh/a`} />
            <Eckdatum label="Netzgebiet" wert={daten.plz ? `PLZ ${daten.plz}` : '–'} />
            <Eckdatum label="Lieferbeginn" wert={fmtDate(daten.lieferbeginn)} />
            <Eckdatum
              label={daten.zaehlverfahren === 'RLM' ? 'Hoechstleistung' : 'Zaehlverfahren'}
              wert={daten.zaehlverfahren === 'RLM'
                ? (daten.hoechstleistung_kw ? `${fmtNum(daten.hoechstleistung_kw)} kW` : '–')
                : (daten.zaehlverfahren || '–')}
            />
          </div>
        </div>

        {/* Hinweis zur Anonymisierung */}
        {anonym && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex gap-3">
            <FiShield className="text-teal-700 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-teal-900 leading-relaxed">
              Die Anbieter sind hier noch neutral dargestellt. Sobald Sie uns beauftragen, legen wir
              offen, welcher Versorger hinter welchem Angebot steht — die Konditionen sehen Sie
              schon jetzt vollstaendig.
            </p>
          </div>
        )}

        {/* Was der Vergleich zeigt — und was bewusst nicht drinsteckt. */}
        {energiebasis && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 flex gap-3">
            <FiInfo className="text-gray-500 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Verglichen wird der Energiepreis</strong> — der Teil
              Ihres Strompreises, den wir fuer Sie verhandeln. Netzentgelte, Abgaben und Umlagen sind
              hier <strong className="text-gray-900">nicht enthalten</strong>: Sie werden vom
              oertlichen Netzbetreiber bzw. vom Gesetzgeber festgelegt, sind bei jedem Versorger
              exakt gleich hoch und deshalb kein Unterscheidungsmerkmal. Auf Ihrer spaeteren Rechnung
              kommen sie zum Energiepreis hinzu.
            </p>
          </div>
        )}

        {/* Steuerung + Vergleichbarkeitshinweis */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            {sortiert.length} {sortiert.length === 1 ? 'Angebot' : 'Angebote'} · sortiert nach{' '}
            <strong className="text-gray-700">{aktiveSortierung.label}</strong>
          </p>
          <div className="inline-flex rounded-lg border bg-white p-1">
            {SORTIERUNGEN.map(s => (
              <button
                key={s.key}
                onClick={() => setSortierung(s.key)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  sortierung === s.key ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {gemischteLaufzeiten && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <FiInfo className="text-amber-700 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-amber-900 leading-relaxed">
              Die Angebote haben unterschiedliche Vertragslaufzeiten
              ({laufzeiten.sort((a, b) => a - b).join(', ')} Monate). Ein laengerer Vertrag bringt
              meist Planungssicherheit, ein kuerzerer mehr Flexibilitaet — die Energiekosten allein
              sagen deshalb noch nicht, welches Angebot das passende ist.
            </p>
          </div>
        )}

        {/* Angebote */}
        {sortiert.length === 0 ? (
          <div className="bg-white rounded-xl border p-10 text-center">
            <p className="text-gray-600 font-medium">Es liegen noch keine Rueckmeldungen vor</p>
            <p className="text-sm text-gray-500 mt-1">
              Sobald die ersten Versorger geantwortet haben, erscheinen ihre Angebote hier.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortiert.map((a, i) => {
              const abgelaufen = fristAbgelaufen(a.bindefrist, a.bindefrist_uhrzeit);
              const infos = Object.entries(TARIF_LABELS)
                .map(([k, label]) => [label, tarifWert(k, a.tarifinfo?.[k])])
                .filter(([, v]) => v != null);
              const hatDetails = infos.length > 0 || !!a.bedingungen;
              const istOffen = !!offen[a.id];
              const abweichend = gemischteLaufzeiten && a.laufzeit_monate;

              return (
                <div key={a.id} className={`bg-white rounded-xl border overflow-hidden ${i === 0 ? 'ring-2 ring-teal-500' : ''}`}>
                  <div className="flex">
                    {/* Rangnummer — folgt IMMER der aktiven Sortierung */}
                    <div className={`w-12 flex-shrink-0 flex items-start justify-center pt-5 ${i === 0 ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                      <span className="font-bold">{i + 1}</span>
                    </div>

                    <div className="flex-1 p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {daten.versorger_sichtbar ? (a.versorger_name || a.anbieter_label) : a.anbieter_label}
                          </p>
                          {daten.versorger_sichtbar && a.produktname && (
                            <p className="text-sm text-gray-500">{a.produktname}</p>
                          )}
                          {i === 0 && (
                            <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-800">
                              {aktiveSortierung.badge}
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">{aktiveSortierung.label}</p>
                          {/* Grosse Kennzahl = Sortierschluessel. Der Zusatz steht direkt
                              darunter: die Zahl ist ausdruecklich nicht der Gesamtpreis. */}
                          <p className="text-2xl font-bold text-teal-700 tabular-nums">
                            {aktiveSortierung.format(a[aktiveSortierung.feld])}
                          </p>
                          {energiebasis && (
                            <p className="text-[11px] text-gray-500 mt-0.5">{ZZGL}</p>
                          )}
                          {a[aktiveSortierung.feld] == null && (
                            <p className="text-xs text-amber-600">Angaben unvollstaendig</p>
                          )}
                        </div>
                      </div>

                      {/* Preisleiste */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-t pt-4">
                        <div>
                          <p className="text-xs text-gray-400">Grundpreis</p>
                          <p className="text-sm font-medium text-gray-900">
                            {a.grundpreis_eur_jahr == null ? '–' : `${fmtEUR(a.grundpreis_eur_jahr)} /Jahr`}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Energiepreis</p>
                          <p className="text-sm font-medium text-gray-900">{fmtCt(a.angebotspreis_ct_kwh)}</p>
                          {energiebasis && (
                            <p className="text-[10px] text-gray-400 leading-tight mt-0.5">{ZZGL}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Leistungspreis</p>
                          <p className="text-sm font-medium text-gray-900">
                            {a.leistungspreis_eur_kw_jahr == null ? '–' : `${fmtEUR(a.leistungspreis_eur_kw_jahr)} /kW`}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Laufzeit</p>
                          <p className="text-sm font-medium text-gray-900">
                            {a.laufzeit_monate ? `${a.laufzeit_monate} Monate` : '–'}
                            {abweichend && laufzeiten.length > 1 && (
                              <span className="ml-1.5 text-xs text-amber-600">abweichend</span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Preisbindefrist — stundengenau */}
                      {a.bindefrist && (
                        <div className={`mt-4 rounded-lg px-4 py-2.5 text-sm flex items-center gap-2 ${
                          abgelaufen ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900'
                        }`}>
                          <FiClock size={15} className="flex-shrink-0" />
                          <span>
                            <strong>Preisbindefrist:</strong> {fmtFrist(a.bindefrist, a.bindefrist_uhrzeit)}
                            {abgelaufen && ' — verstrichen, der Preis muss vom Versorger neu bestaetigt werden.'}
                          </span>
                        </div>
                      )}

                      {/* Tarifinformationen */}
                      {hatDetails && (
                        <div className="mt-4 border-t pt-3">
                          <button
                            onClick={() => setOffen(o => ({ ...o, [a.id]: !o[a.id] }))}
                            className="flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-800"
                          >
                            Tarifinformationen
                            {istOffen ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
                          </button>
                          {istOffen && (
                            <dl className="mt-3 space-y-2">
                              {infos.map(([label, wert]) => (
                                <div key={label} className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-sm">
                                  <dt className="text-gray-500">{label}</dt>
                                  <dd className="sm:col-span-2 text-gray-800">{wert}</dd>
                                </div>
                              ))}
                              {a.bedingungen && (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-sm">
                                  <dt className="text-gray-500">Bemerkung</dt>
                                  <dd className="sm:col-span-2 text-gray-800 whitespace-pre-line">{a.bedingungen}</dd>
                                </div>
                              )}
                            </dl>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-gray-400 leading-relaxed">
          Alle Preise netto zzgl. Umsatzsteuer.{' '}
          {energiebasis
            ? 'Ausgewiesen ist der Energiepreis; Netzentgelte, Abgaben und Umlagen sind darin NICHT enthalten. '
              + 'Sie werden vom Netzbetreiber bzw. vom Gesetzgeber festgelegt, fallen bei jedem Anbieter in gleicher '
              + 'Hoehe an und kommen zusaetzlich hinzu. Die Energiekosten/Jahr sind auf Basis Ihres angegebenen '
              + 'Jahresverbrauchs hochgerechnet; der tatsaechliche Betrag richtet sich nach Ihrem realen Verbrauch.'
            : 'Die Jahreskosten sind auf Basis Ihres angegebenen Jahresverbrauchs hochgerechnet; der tatsaechliche '
              + 'Betrag richtet sich nach Ihrem realen Verbrauch.'}{' '}
          Versorgerpreise sind nur innerhalb der jeweiligen Preisbindefrist gueltig.
        </p>

        <footer className="border-t pt-5 text-sm text-gray-500">
          Fragen zum Vergleich? <a href="mailto:energie@aiqcon.com" className="text-teal-700 font-medium">energie@aiqcon.com</a>
        </footer>
      </main>
    </div>
  );
};

export default VersorgerVergleich;
